import * as types from '../types';
import { IOnlinePaymentsApi } from '../contracts';
import { HttpApi } from './HttpApi';

const BASE = '/online-payments';
const enc = encodeURIComponent;

export class OnlinePaymentsHttpApi extends HttpApi implements IOnlinePaymentsApi {
  async getProcessorConnection(): Promise<types.ProcessorConnection> {
    return (await this.http.get<types.APIResponse<types.ProcessorConnection>>(`${BASE}/connection`)).data.data;
  }

  async startProcessorConnect(): Promise<{ url: string; provider: string }> {
    return (await this.http.post<types.APIResponse<{ url: string; provider: string }>>(`${BASE}/connection/start`, {})).data.data;
  }

  async chooseProcessorLocation(locationId: string): Promise<types.ProcessorConnection> {
    return (await this.http.post<types.APIResponse<types.ProcessorConnection>>(`${BASE}/connection/location`, { locationId })).data.data;
  }

  async disconnectProcessor(): Promise<types.ProcessorConnection & { revokedAtProcessor: boolean }> {
    const response = await this.http.post<types.APIResponse<types.ProcessorConnection & { revokedAtProcessor: boolean }>>(
      `${BASE}/connection/disconnect`,
      {}
    );
    return response.data.data;
  }

  async getOnlinePaymentSettings(): Promise<types.OnlinePaymentSettings> {
    return (await this.http.get<types.APIResponse<types.OnlinePaymentSettings>>(`${BASE}/settings`)).data.data;
  }

  async updateOnlinePaymentSettings(data: types.UpdateOnlinePaymentSettingsRequest): Promise<types.OnlinePaymentSettings> {
    return (await this.http.put<types.APIResponse<types.OnlinePaymentSettings>>(`${BASE}/settings`, data)).data.data;
  }

  async getPublicPayConfig(token: string): Promise<types.PublicPayConfig> {
    return (await this.http.get<types.APIResponse<types.PublicPayConfig>>(`/public/invoices/${enc(token)}/pay-config`)).data.data;
  }

  async payPublicInvoice(token: string, data: types.PublicPayRequest): Promise<types.PublicPayResult> {
    return (await this.http.post<types.APIResponse<types.PublicPayResult>>(`/public/invoices/${enc(token)}/pay`, data)).data.data;
  }

  async listPaymentMethods(customerId: string): Promise<types.CustomerPaymentMethod[]> {
    return (await this.http.get<types.APIResponse<types.CustomerPaymentMethod[]>>(`/customers/${enc(customerId)}/payment-methods`)).data.data;
  }

  async addPaymentMethod(
    customerId: string,
    data: { sourceId: string; makeDefault?: boolean; autopayConsent?: types.AutopayConsentInput }
  ): Promise<types.CustomerPaymentMethod> {
    return (await this.http.post<types.APIResponse<types.CustomerPaymentMethod>>(`/customers/${enc(customerId)}/payment-methods`, data)).data.data;
  }

  async setPaymentMethodAutopay(
    customerId: string,
    methodId: string,
    data: { enabled: boolean; autopayConsent?: types.AutopayConsentInput }
  ): Promise<types.CustomerPaymentMethod> {
    const url = `/customers/${enc(customerId)}/payment-methods/${enc(methodId)}/autopay`;
    return (await this.http.put<types.APIResponse<types.CustomerPaymentMethod>>(url, data)).data.data;
  }

  async setDefaultPaymentMethod(customerId: string, methodId: string): Promise<types.CustomerPaymentMethod> {
    const url = `/customers/${enc(customerId)}/payment-methods/${enc(methodId)}/default`;
    return (await this.http.put<types.APIResponse<types.CustomerPaymentMethod>>(url, {})).data.data;
  }

  async removePaymentMethod(customerId: string, methodId: string): Promise<types.CustomerPaymentMethod> {
    const url = `/customers/${enc(customerId)}/payment-methods/${enc(methodId)}`;
    return (await this.http.delete<types.APIResponse<types.CustomerPaymentMethod>>(url)).data.data;
  }

  async chargeInvoiceCard(invoiceId: string, data: types.StaffChargeRequest): Promise<types.ChargeOutcome> {
    return (await this.http.post<types.APIResponse<types.ChargeOutcome>>(`/invoices/${enc(invoiceId)}/charge`, data)).data.data;
  }

  async refundPayment(paymentId: string, reason: string): Promise<types.RefundResult> {
    return (await this.http.post<types.APIResponse<types.RefundResult>>(`/payments/${enc(paymentId)}/refund`, { reason })).data.data;
  }

  async listOnlinePaymentAttempts(
    filters: types.OnlinePaymentAttemptFilters = {},
    page = 1,
    limit = 50
  ): Promise<types.OnlinePaymentAttemptList> {
    // Blank filters are left out: the server rejects e.g. status='' as invalid
    const params: Record<string, string | number> = { page, limit };
    (['status', 'source', 'customerId', 'invoiceId', 'startDate', 'endDate'] as const).forEach((key) => {
      const value = filters[key];
      if (value) params[key] = value;
    });
    if (filters.needsAttention) params.needsAttention = 'true';
    return (await this.http.get<types.OnlinePaymentAttemptList>(`${BASE}/attempts`, { params })).data;
  }

  async reconcileOnlinePaymentAttempt(id: string, resolution?: 'NOT_CHARGED'): Promise<types.OnlinePaymentAttempt> {
    const response = await this.http.post<types.APIResponse<types.OnlinePaymentAttempt>>(
      `${BASE}/attempts/${enc(id)}/reconcile`,
      resolution ? { resolution } : {}
    );
    return response.data.data;
  }
}
